
import { Button } from "@/components/ui/button";
import { Send, RefreshCw, ChevronDown, ChevronUp, Paperclip } from "lucide-react";
import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface ResponseActionsProps {
  onRegenerate: () => void;
  onSend: (options: { subject: string; cc?: string; bcc?: string }) => void;
  onAttachmentAdd?: (file: File) => void;
  isGenerating: boolean;
  emailSent: boolean;
  attachments?: File[];
}

const ResponseActions = ({ 
  onRegenerate, 
  onSend, 
  onAttachmentAdd, 
  isGenerating, 
  emailSent,
  attachments = [] 
}: ResponseActionsProps) => {
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    // Validate inputs to prevent potential encoding issues
    console.log("Sending email with subject:", subject);
    onSend({ subject, cc, bcc });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) { // 25MB limit (Gmail's attachment size limit)
        toast.error("File too large. Maximum attachment size is 25MB.");
        return;
      }
      
      if (onAttachmentAdd) {
        onAttachmentAdd(file);
        toast.success(`Attachment added: ${file.name}`);
      }
    }
    
    // Clear the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onRegenerate}
          disabled={isGenerating}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? "animate-spin" : ""}`} />
          Regenerate
        </Button>
        
        <input 
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          accept="*/*" // Accept all file types
        />
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={emailSent}
        >
          <Paperclip className="h-4 w-4 mr-2" />
          {attachments.length > 0 ? `Attachments (${attachments.length})` : "Add Attachment"}
        </Button>
        
        <button 
          onClick={() => setShowCcBcc(!showCcBcc)}
          className="text-sm text-gray-600 hover:text-blue-500 flex items-center transition-colors"
        >
          CC/BCC {showCcBcc ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
        </button>

        <Button 
          size="sm" 
          onClick={handleSend}
          disabled={emailSent}
          variant={emailSent ? "secondary" : "default"}
        >
          <Send className="h-4 w-4 mr-2" />
          {emailSent ? "Email Sent" : "Send Response"}
        </Button>
      </div>

      {attachments.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-sm text-gray-600">Attachments:</p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <div key={index} className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-2">
                <Paperclip className="h-3 w-3" />
                <span className="truncate max-w-[150px]">{file.name}</span>
                <span className="text-xs text-gray-500">
                  {(file.size / 1024 < 1024) 
                    ? `${Math.round(file.size / 1024 * 10) / 10}KB` 
                    : `${Math.round(file.size / 1024 / 1024 * 10) / 10}MB`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showCcBcc && (
        <div className="space-y-2">
          <Input
            placeholder="Subject"
            value={subject}
            onChange={(e) => {
              console.log("Subject changed:", e.target.value);
              setSubject(e.target.value);
            }}
            className="w-full"
            lang="auto"
            dir="auto"
            inputMode="text"
            autoComplete="off"
            spellCheck="false"
          />
          <Input
            placeholder="CC (comma-separated emails)"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            className="w-full"
            lang="auto"
            dir="auto"
          />
          <Input
            placeholder="BCC (comma-separated emails)"
            value={bcc}
            onChange={(e) => setBcc(e.target.value)}
            className="w-full"
            lang="auto"
            dir="auto"
          />
        </div>
      )}
    </div>
  );
};

export default ResponseActions;
